import type { ReactNode, SelectHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

import { CONTROL_CLASS, FieldShell } from './Field';
import { Icon } from './Icon';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string = string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'children'> {
  label: ReactNode;
  options: readonly SelectOption<T>[];
  hint?: ReactNode;
  error?: string | null;
  /** Rendered first with an empty value, for a nullable field. */
  placeholder?: string;
  wrapperClassName?: string;
}

/**
 * A native `<select>`, on purpose.
 *
 * The lists this app picks from are short and closed - four lanes, seven
 * stages, ten point values, a roster of four - and the native control brings
 * keyboard handling, a real mobile picker and screen-reader support that a
 * custom listbox would have to earn back. The chevron is ours; the behaviour
 * is the platform's.
 */
export function Select<T extends string = string>({
  label,
  options,
  hint,
  error,
  placeholder,
  required,
  className,
  wrapperClassName,
  ...rest
}: SelectProps<T>) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      {({ id, describedBy, invalid }) => (
        <div className="relative">
          <select
            id={id}
            required={required}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={cn(
              CONTROL_CLASS,
              'h-10 cursor-pointer appearance-none pr-9',
              invalid ? 'border-danger' : 'border-line-strong hover:border-ink/25',
              className,
            )}
            {...rest}
          >
            {placeholder ? <option value="">{placeholder}</option> : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <Icon
            name="chevronDown"
            size={15}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-subtle"
          />
        </div>
      )}
    </FieldShell>
  );
}
